import type { PlatformFilter } from '../../../entities/account';
import type { OverviewLatest } from '../../../features/statistics/api/overview';
import type { AccountStatsRow } from '../../../features/statistics/types/account-stats';

export type TikTokTotals = {
  accounts: number;
  views: number;
  interactions: number;
  followers: number;
};

/**
 * Суммы по TikTok-строкам для карточек сводки. Строки приходят «посевом»:
 * сначала с null-метриками, затем догружаются по одной — поэтому null считаем
 * нулём, но сам аккаунт учитываем сразу.
 * Охвата (reach) у TikTok нет — Display API его не отдаёт, суммы для него нет.
 */
export function sumTikTokTotals(rows: AccountStatsRow[]): TikTokTotals {
  return rows.reduce<TikTokTotals>(
    (a, r) => ({
      accounts: a.accounts + 1,
      views: a.views + (r.impressions ?? 0),
      interactions: a.interactions + (r.interactions ?? 0),
      followers: a.followers + (r.followers ?? 0),
    }),
    { accounts: 0, views: 0, interactions: 0, followers: 0 },
  );
}

export type OverviewTotals = {
  activeAccounts: number;
  /** Всего аккаунтов (знаменатель «из N всего»); null — список ещё не загружен. */
  totalAccounts: number | null;
  /** null = метрики нет у выбранной платформы (у TikTok нет охвата). */
  reach: number | null;
  views: number;
  interactions: number;
  followers: number;
  er: number | null;
  /** Что именно посчитано в ER — подпись под карточкой. */
  erNote: string;
  /** Показывать ли график топа по охвату (он Instagram-only). */
  showReachChart: boolean;
};

/**
 * Числа для карточек сводки с учётом фильтра платформы.
 * Instagram приходит агрегатом от почасового крона (OverviewLatest), TikTok —
 * живыми строками; поэтому склейка делается здесь, а не на бэкенде.
 * ER платформ не смешиваем: у Instagram знаменатель — охват, у TikTok —
 * просмотры видео, сумма таких процентов смысла не имеет.
 */
export function selectOverviewTotals(args: {
  latest: OverviewLatest | null;
  tiktok: TikTokTotals;
  /** Instagram-аккаунты из стора — знаменатель «из N всего». */
  instagramAccounts: number;
  filter: PlatformFilter;
}): OverviewTotals {
  const { latest, tiktok, instagramAccounts, filter } = args;

  if (filter === 'tiktok') {
    const er = tiktok.views > 0 ? (tiktok.interactions / tiktok.views) * 100 : null;
    return {
      activeAccounts: tiktok.accounts,
      totalAccounts: tiktok.accounts,
      reach: null,
      views: tiktok.views,
      interactions: tiktok.interactions,
      followers: tiktok.followers,
      er,
      erNote: 'по просмотрам',
      showReachChart: false,
    };
  }

  const ig = {
    activeAccounts: latest?.active_accounts ?? 0,
    reach: latest?.total_reach ?? 0,
    views: latest?.total_views ?? 0,
    interactions: latest?.total_interactions ?? 0,
    followers: latest?.total_followers ?? 0,
  };

  if (filter === 'instagram') {
    return {
      ...ig,
      totalAccounts: instagramAccounts > 0 ? instagramAccounts : null,
      er: latest?.avg_er ?? null,
      erNote: 'по охвату',
      showReachChart: true,
    };
  }

  // filter === 'all'
  const totalAccounts = instagramAccounts + tiktok.accounts;
  return {
    activeAccounts: ig.activeAccounts + tiktok.accounts,
    totalAccounts: totalAccounts > 0 ? totalAccounts : null,
    // Охват остаётся Instagram-only: у TikTok такой метрики нет вовсе.
    reach: ig.reach,
    views: ig.views + tiktok.views,
    interactions: ig.interactions + tiktok.interactions,
    followers: ig.followers + tiktok.followers,
    er: latest?.avg_er ?? null,
    erNote: tiktok.accounts > 0 ? 'Instagram, по охвату' : 'по охвату',
    showReachChart: true,
  };
}
